import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Upload, AlertTriangle, RotateCcw, MessageCircle, Calculator, BookOpen, Phone, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Header } from '../components/Layout';

const SUSTANCIAS = ['Alcohol', 'Cocaína', 'Pasta Base', 'Marihuana', 'Benzodiacepinas / Opioides'];

// Mensaje de prevención específico por sustancia, mostrado en el reveal
const PREVENTION_MESSAGES = {
  'Alcohol': {
    title: 'El alcohol mata más chilenos que todas las drogas ilegales juntas.',
    body: 'Es la única droga legal y por eso la más subestimada. Provoca cirrosis, demencia y miles de muertes evitables al año en Chile.',
  },
  'Cocaína': {
    title: 'Cada línea daña tu corazón, tabique y cerebro — sin avisar.',
    body: 'La cocaína mata por infarto fulminante a personas jóvenes y sanas. La dependencia se instala en meses, no años.',
  },
  'Pasta Base': {
    title: 'La pasta base destruye en meses lo que tomó años construir.',
    body: 'Es la sustancia con mayor capacidad adictiva del mercado chileno. El tratamiento residencial supervisado es la opción más efectiva en estos casos.',
  },
  'Marihuana': {
    title: 'El consumo diario en adolescentes reduce el CI de forma permanente.',
    body: 'Aumenta 4 veces el riesgo de psicosis y trastornos depresivos. La marihuana de hoy tiene 5x más THC que la de hace 20 años.',
  },
  'Benzodiacepinas / Opioides': {
    title: 'Los medicamentos pueden ser tan adictivos como las drogas duras.',
    body: 'La dependencia química es invisible hasta que es grave. La discontinuación abrupta puede causar convulsiones — siempre debe ser supervisada por un médico.',
  },
};

const LOADING_MESSAGES = [
  'Procesando tu rostro...',
  'La adicción no es falta de voluntad. Es una enfermedad del cerebro.',
  'Cada consumo modifica las conexiones neuronales que controlan tus decisiones.',
  'El cerebro de un adicto no responde al placer común. Necesita más para sentir lo mismo.',
  'En Chile, 1 de cada 4 personas con adicción muere antes de buscar ayuda.',
  'Tu rostro está revelándose...',
  'Respira. Esto es una proyección. No tu destino.',
];

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getYear() { return new Date().getFullYear(); }

// ============================================================================
// FASE 1: INTRO
// ============================================================================
function Intro({ onContinue }) {
  return (
    <motion.div
      key="intro"
      className="fixed inset-0 z-[60] bg-black flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Noise + scanlines */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)',
      }} />
      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.85) 100%)' }} />

      <div className="relative z-10 flex flex-col items-center px-6 text-center max-w-xl">
        <motion.p
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-6 font-mono"
        >
          &gt; ADVERTENCIA · SIMULACIÓN VISUAL
        </motion.p>
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-4xl sm:text-6xl font-light text-white mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '2px 0 #ff3b3b, -2px 0 #00e5ff' }}
        >
          Mírate<br />en 5 años.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.6 }}
          className="text-base sm:text-lg text-neutral-300 mb-10 leading-relaxed"
        >
          Si no detienes el consumo, tu cuerpo te lo hará saber. <br className="hidden sm:block" />
          Esta simulación visualiza cómo cambiarías tras 5 años de adicción severa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="flex flex-col items-center gap-4"
        >
          <button
            onClick={onContinue}
            data-testid="espejo-intro-start"
            className="group px-10 py-4 bg-red-500 hover:bg-red-400 text-black font-bold uppercase tracking-[0.2em] text-sm rounded-md transition-all hover:scale-105 shadow-[0_0_60px_rgba(239,68,68,0.5)] font-mono inline-flex items-center gap-2"
          >
            Comenzar
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-neutral-500 font-mono max-w-md">
            * Tu foto se procesa y se descarta. NO se guarda en ningún servidor.
            Esta simulación es generada por IA con fines educativos.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// FASE 2: FORMULARIO + UPLOAD
// ============================================================================
function IntakeForm({ onSubmit }) {
  const [nombre, setNombre] = useState('');
  const [edad, setEdad] = useState(28);
  const [sustancia, setSustancia] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  function handlePhotoChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen');
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError('La imagen no puede pesar más de 8 MB');
      return;
    }
    setError('');
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(f);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!nombre.trim() || nombre.trim().length < 2) { setError('Ingresa tu nombre'); return; }
    if (!edad || edad < 14 || edad > 80) { setError('Edad inválida (14-80)'); return; }
    if (!sustancia) { setError('Selecciona una sustancia'); return; }
    if (!photoFile) { setError('Sube una foto tuya'); return; }
    setError('');
    onSubmit({ nombre: nombre.trim(), edad: Number(edad), sustancia, photoFile });
  }

  return (
    <motion.form
      key="form"
      onSubmit={handleSubmit}
      className="fixed inset-0 z-[60] bg-black flex items-center justify-center p-6 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full max-w-lg bg-slate-900/90 backdrop-blur border border-slate-800 rounded-2xl p-6 sm:p-8 my-8">
        <h2 className="text-2xl sm:text-3xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Tus datos para la simulación
        </h2>
        <p className="text-sm text-slate-400 mb-6">Confidencial. Tu foto se procesa y se descarta.</p>

        <div className="space-y-4">
          <div>
            <Label className="text-slate-300 mb-1.5 block text-sm">Nombre</Label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Tu nombre"
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="espejo-input-nombre"
              maxLength={40}
            />
          </div>

          <div>
            <Label className="text-slate-300 mb-1.5 block text-sm">Edad</Label>
            <input
              type="number"
              min={14}
              max={80}
              value={edad}
              onChange={(e) => setEdad(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="espejo-input-edad"
            />
          </div>

          <div>
            <Label className="text-slate-300 mb-1.5 block text-sm">Sustancia o tipo de adicción</Label>
            <select
              value={sustancia}
              onChange={(e) => setSustancia(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="espejo-input-sustancia"
            >
              <option value="">Selecciona una</option>
              {SUSTANCIAS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>

          <div>
            <Label className="text-slate-300 mb-1.5 block text-sm">Tu foto (selfie de frente)</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoChange}
              className="hidden"
              data-testid="espejo-input-photo"
            />
            {!photoPreview ? (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-700 hover:border-red-500/60 rounded-lg p-8 text-center transition-colors group"
                data-testid="espejo-photo-trigger"
              >
                <Upload className="w-10 h-10 text-slate-500 mx-auto mb-2 group-hover:text-red-400 transition-colors" />
                <p className="text-sm text-slate-300 font-medium">Subir mi foto</p>
                <p className="text-xs text-slate-500 mt-1">JPG, PNG o WEBP · máx 8 MB</p>
              </button>
            ) : (
              <div className="relative rounded-lg overflow-hidden border border-slate-700" data-testid="espejo-photo-preview">
                <img src={photoPreview} alt="Tu foto" className="w-full max-h-72 object-contain bg-slate-950" />
                <button
                  type="button"
                  onClick={() => { setPhotoFile(null); setPhotoPreview(null); if (fileRef.current) fileRef.current.value = ''; }}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center"
                  aria-label="Quitar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {error && (
            <p className="text-red-400 text-sm flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{error}</p>
          )}

          <Button
            type="submit"
            className="w-full bg-red-500 hover:bg-red-400 text-black font-bold py-6 text-base font-mono uppercase tracking-widest"
            data-testid="espejo-form-submit"
          >
            Ver mi futuro <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-[11px] text-slate-500 leading-relaxed text-center">
            Al continuar aceptas que tu imagen se procese temporalmente con IA. La foto NO se almacena.
          </p>
        </div>
      </div>
    </motion.form>
  );
}

// ============================================================================
// FASE 3: LOADING + MENSAJES
// ============================================================================
function LoadingPhase({ ready, error }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      key="loading"
      className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center overflow-hidden p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)',
      }} />

      {/* Pulsing silhouette */}
      <motion.div
        animate={{ scale: [1, 1.06, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-radial from-red-500/40 via-red-700/20 to-transparent blur-2xl mb-8"
      />

      <p className="text-[10px] uppercase tracking-[0.4em] text-cyan-400 font-mono mb-4">&gt; GENERANDO PROYECCIÓN</p>

      <div className="max-w-xl text-center min-h-[5rem] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={idx}
            initial={{ opacity: 0, y: 12, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -12, filter: 'blur(6px)' }}
            transition={{ duration: 0.6 }}
            className="text-lg sm:text-2xl text-white font-light leading-relaxed"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {LOADING_MESSAGES[idx]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="w-64 h-[2px] bg-slate-800 rounded-full mt-12 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-red-500 via-cyan-400 to-red-500"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />
      </div>
      <p className="text-xs text-slate-500 font-mono mt-3">{ready ? 'Casi listo...' : 'Procesando con IA · 12-20s'}</p>

      {error && (
        <div className="mt-6 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg max-w-md text-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </motion.div>
  );
}

// ============================================================================
// FASE 4: REVELACIÓN
// ============================================================================
function RevealPhase({ data, onContinue }) {
  const prevention = PREVENTION_MESSAGES[data.sustancia] || null;
  return (
    <motion.div
      key="reveal"
      className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center overflow-y-auto p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)',
      }} />

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-[10px] uppercase tracking-[0.4em] text-red-400 font-mono mb-4"
      >
        &gt; PROYECCIÓN AÑO {getYear() + 5}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.92, filter: 'blur(20px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.4 }}
        className="relative max-w-md w-full mb-6"
        data-testid="espejo-reveal-image"
      >
        <img
          src={`data:${data.mime_type};base64,${data.image_base64}`}
          alt={`${data.nombre} en ${getYear() + 5}`}
          className="w-full rounded-lg border-2 border-red-500/30"
          style={{ filter: 'contrast(1.05) saturate(0.9)' }}
        />
        {/* Chromatic aberration overlay */}
        <div className="pointer-events-none absolute inset-0 rounded-lg" style={{
          boxShadow: 'inset 0 0 80px rgba(239, 68, 68, 0.3), 4px 0 0 rgba(255, 59, 59, 0.15), -4px 0 0 rgba(0, 229, 255, 0.15)',
          mixBlendMode: 'screen',
        }} />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.7 }}
        className="text-xl sm:text-3xl text-white text-center mb-3 font-light max-w-2xl"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {data.nombre}, así podrías verte en {getYear() + 5}<br />si no cambias nada.
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="text-xs text-slate-500 max-w-md text-center mb-6 font-mono"
      >
        Simulación generada por IA basada en patrones reales de deterioro físico.
      </motion.p>

      {/* Mensaje de prevención específico por sustancia */}
      {prevention && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.6, duration: 0.7 }}
          className="max-w-xl w-full bg-red-950/30 border border-red-500/40 rounded-xl p-5 sm:p-6 mb-8"
          data-testid="espejo-prevention-message"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-300 font-mono mb-2">
            &gt; Sobre {data.sustancia}
          </p>
          <p
            className="text-base sm:text-xl text-white font-light leading-snug mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {prevention.title}
          </p>
          <p className="text-sm text-slate-300 leading-relaxed">
            {prevention.body}
          </p>
        </motion.div>
      )}

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.4, duration: 0.6 }}
        onClick={onContinue}
        data-testid="espejo-reveal-continue"
        className="px-8 py-3 bg-neutral-900 hover:bg-neutral-800 text-white border border-neutral-700 font-mono uppercase tracking-widest text-sm rounded-md inline-flex items-center gap-2 transition"
      >
        Continuar
        <ArrowRight className="w-4 h-4" />
      </motion.button>
    </motion.div>
  );
}

// ============================================================================
// FASE 5: TUMBA + CTA FINAL
// ============================================================================
function TombstoneCanvas({ nombre }) {
  // Renderiza la lápida con nombre overlayado vía Canvas
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      // Engraving area: aproximadamente el centro horizontal, 35-50% vertical
      const cx = canvas.width / 2;
      const cy = canvas.height * 0.42;
      // Nombre — fuente serif tipo grabado
      const nameFont = `bold ${Math.round(canvas.width * 0.062)}px 'Cinzel', 'Cormorant Garamond', Georgia, serif`;
      ctx.font = nameFont;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#1a1a1a';
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 1;
      // Trim and uppercase
      const cleanName = (nombre || 'TÚ').toUpperCase().slice(0, 20);
      ctx.fillText(cleanName, cx, cy);
      // Fechas debajo
      ctx.font = `${Math.round(canvas.width * 0.028)}px 'Cinzel', Georgia, serif`;
      ctx.fillText(`${getYear()} — ${getYear() + 5}`, cx, cy + canvas.width * 0.075);
      // Cross/symbol
      ctx.font = `${Math.round(canvas.width * 0.04)}px serif`;
      ctx.fillText('†', cx, cy - canvas.width * 0.075);
      setReady(true);
    };
    img.onerror = () => setReady(true);
    img.src = '/assets/espejo/lapida.jpg';
  }, [nombre]);

  return (
    <div className="relative w-full max-w-md mx-auto">
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg border border-slate-800 shadow-2xl"
        style={{ opacity: ready ? 1 : 0, transition: 'opacity 1s' }}
        data-testid="espejo-tombstone-canvas"
      />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 rounded-lg">
          <div className="w-8 h-8 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

function FinalPhase({ nombre, onRestart }) {
  return (
    <motion.div
      key="final"
      className="fixed inset-0 z-[40] bg-black overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
      data-testid="espejo-final"
    >
      <Header />
      <div className="min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1 }}
        >
          <TombstoneCanvas nombre={nombre} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.0, duration: 1 }}
          className="text-center mt-10"
        >
          <p
            className="text-2xl sm:text-4xl text-white font-light leading-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            La adicción no es un juego.
          </p>
          <p
            className="text-2xl sm:text-4xl text-red-400 font-light leading-tight mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            La adicción mata personas.
          </p>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-2">
            Si tú o alguien cercano tiene problemas de adicción,<br className="hidden sm:block" /> pide ayuda.
          </p>
          <p className="text-emerald-400 font-medium text-base sm:text-lg mb-10">
            Nosotros te guiamos.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.8, duration: 0.8 }}
          className="w-full space-y-3"
        >
          <Link
            to="/asesoria"
            onClick={onRestart}
            data-testid="espejo-cta-asesoria"
            className="group relative w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wider text-sm rounded-xl transition-all shadow-[0_0_60px_rgba(245,158,11,0.4)] hover:scale-[1.02]"
          >
            <Phone className="w-5 h-5" />
            Quiero asesoría experta
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            <motion.div
              className="absolute inset-0 rounded-xl bg-amber-400/30"
              animate={{ scale: [1, 1.04, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </Link>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to="/cotizar"
              onClick={onRestart}
              data-testid="espejo-cta-cotizar"
              className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-emerald-500/40 hover:border-emerald-500 text-emerald-300 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 transition"
            >
              <MessageCircle className="w-4 h-4" />
              Cotizar centros
            </Link>
            <Link
              to="/blog"
              onClick={onRestart}
              data-testid="espejo-cta-blog"
              className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-cyan-500/40 hover:border-cyan-500 text-cyan-300 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 transition"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>
            <Link
              to="/calculadora"
              onClick={onRestart}
              data-testid="espejo-cta-calc"
              className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-purple-500/40 hover:border-purple-500 text-purple-300 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 transition"
            >
              <Calculator className="w-4 h-4" />
              Herramientas
            </Link>
          </div>

          {/* Líneas de ayuda 24/7 */}
          <div className="mt-4 bg-red-950/20 border border-red-900/40 rounded-lg p-4 text-left" data-testid="espejo-helplines">
            <p className="text-red-200 text-[11px] uppercase tracking-widest font-mono mb-2 text-center">
              Si necesitas ayuda <span className="text-red-300">ahora</span>
            </p>
            <div className="space-y-1.5 text-xs sm:text-sm text-neutral-200 leading-relaxed">
              <p>
                <a href="tel:1412" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SENDA · 1412</a>
                <span className="text-slate-400"> — orientación en adicciones, gratis 24/7</span>
              </p>
              <p>
                <a href="tel:6003607777" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">Salud Responde · 600 360 7777</a>
                <span className="text-slate-400"> — apoyo emocional y crisis suicida</span>
              </p>
              <p>
                <a href="tel:131" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SAMU · 131</a>
                <span className="text-slate-400"> — emergencia médica</span>
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onRestart}
            data-testid="espejo-restart"
            className="w-full text-slate-500 hover:text-slate-300 text-sm py-3 inline-flex items-center justify-center gap-2 transition"
          >
            <RotateCcw className="w-4 h-4" />
            Empezar de nuevo
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// FASE ERROR (rate limit / falla de IA / red)
// ============================================================================
function ErrorPhase({ error, onRetry, onSkip }) {
  return (
    <motion.div
      key="error"
      className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center p-6 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="espejo-error-phase"
    >
      <div className="w-full max-w-md bg-slate-900/80 border border-red-500/40 rounded-2xl p-6 sm:p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-red-400" />
        </div>
        <h2
          className="text-xl sm:text-2xl text-white font-light mb-3"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          No pudimos generar tu simulación
        </h2>
        <p className="text-sm text-slate-300 leading-relaxed mb-6">
          {error || 'Algo falló al procesar tu foto. Por favor intenta de nuevo.'}
        </p>
        <div className="space-y-2.5">
          <Button
            onClick={onRetry}
            className="w-full bg-red-500 hover:bg-red-400 text-black font-bold py-5 text-sm font-mono uppercase tracking-widest"
            data-testid="espejo-error-retry"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Intentar de nuevo
          </Button>
          <button
            type="button"
            onClick={onSkip}
            className="w-full text-slate-400 hover:text-slate-200 text-sm py-2 underline underline-offset-4"
            data-testid="espejo-error-skip"
          >
            Ver el mensaje final igualmente
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================
export default function ExperienciaEspejo() {
  const [phase, setPhase] = useState('intro'); // intro | form | loading | reveal | final
  const [data, setData] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Hide global floating buttons during this experience
  useEffect(() => {
    document.body.classList.add('experience-active');
    return () => document.body.classList.remove('experience-active');
  }, []);

  function reset() {
    setPhase('intro');
    setData({});
    setResult(null);
    setError('');
  }

  async function startGeneration(payload) {
    setData(payload);
    setPhase('loading');
    setError('');
    try {
      const fd = new FormData();
      fd.append('photo', payload.photoFile);
      fd.append('nombre', payload.nombre);
      fd.append('edad', String(payload.edad));
      fd.append('sustancia', payload.sustancia);

      const res = await fetch(`${API_URL}/api/experiencias/face-aging`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        let detail = 'No pudimos generar la simulación. Intenta de nuevo.';
        try { const j = await res.json(); detail = j.detail || detail; } catch (e) { /* ignore */ }
        // Cualquier error (incl. rate limit 429) muestra la pantalla de error con opción de reintentar
        // o saltar al mensaje final. NO saltamos al final automáticamente.
        setError(detail);
        setPhase('error');
        return;
      }
      const json = await res.json();
      setResult(json);
      // Asegurar mínimo 12s de loading para que los mensajes se vean (UX intencional)
      setPhase('reveal');
    } catch (e) {
      console.error('espejo gen error', e);
      setError(e.message || 'No pudimos generar la simulación. Por favor intenta de nuevo en unos minutos.');
      // NO saltamos automáticamente al final. Mostramos el error al usuario y le damos opción de reintentar.
      setPhase('error');
    }
  }

  return (
    <>
      
        <title>Mírate en 5 años — Experiencia inmersiva con IA | SinAdicciones.org</title>
        <meta name="description" content="Sube tu foto y mira cómo te verías tras 5 años de adicción. Experiencia inmersiva con IA. Tu foto no se guarda. SinAdicciones.org" />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href="https://sinadicciones.org/espejo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/espejo" />
        <meta property="og:title" content="Mírate en 5 años — Una simulación que podría salvar una vida" />
        <meta property="og:description" content="Sube tu foto y mira cómo te verías tras 5 años de adicción. Experiencia inmersiva con IA. SinAdicciones.org" />
        <meta property="og:image" content="https://sinadicciones.org/assets/experiencias/card_espejo.jpg" />
        <meta property="og:image:secure_url" content="https://sinadicciones.org/assets/experiencias/card_espejo.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="746" />
        <meta property="og:image:alt" content="Mírate en 5 años - Experiencia inmersiva sobre adicciones" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mírate en 5 años — Una simulación que podría salvar una vida" />
        <meta name="twitter:description" content="Sube tu foto y mira cómo te verías tras 5 años de adicción. Experiencia inmersiva con IA." />
        <meta name="twitter:image" content="https://sinadicciones.org/assets/experiencias/card_espejo.jpg" />
      

      <AnimatePresence mode="wait">
        {phase === 'intro' && <Intro key="intro" onContinue={() => { setPhase('form'); import('../lib/track').then(m => m.trackEvent('espejo_started', '/espejo')); }} />}
        {phase === 'form' && <IntakeForm key="form" onSubmit={startGeneration} />}
        {phase === 'loading' && <LoadingPhase key="loading" ready={!!result} error={error} />}
        {phase === 'reveal' && result && <RevealPhase key="reveal" data={{ ...result, sustancia: data.sustancia }} onContinue={() => { setPhase('final'); import('../lib/track').then(m => m.trackEvent('espejo_completed', '/espejo')); }} />}
        {phase === 'error' && (
          <ErrorPhase
            key="error"
            error={error}
            onRetry={() => { setError(''); setResult(null); setPhase('form'); }}
            onSkip={() => setPhase('final')}
          />
        )}
        {phase === 'final' && <FinalPhase key="final" nombre={data.nombre} onRestart={reset} />}
      </AnimatePresence>
    </>
  );
}
